/**
 * safeZone.ts -- Phase 48: Safe zone module.
 *
 * Right-edge safe zone that clears all enemies when entered (3 uses per run).
 * State machine: idle -> fighting (stagger-explode enemies) -> returning -> idle.
 * 4th entry attempt shows floating taunt box since dialogue box is gone.
 */

import type { GameState } from './types';
import { spawnParticle } from './particles';
import { queueLine, setSafeZoneLevel } from './dialogue';
import { setCharacterState } from './character';
import { SAFE_ZONE_QUIPS, SAFE_ZONE_TAUNT } from './dialogueScript';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SAFE_ZONE_WIDTH = 72;            // CSS px, right-aligned
const SAFE_ZONE_TAUNT_DURATION = 240;  // frames (4s at 60fps)
const STAGGER_INTERVAL = 8;            // frames between enemy explosions
const FIGHTING_MIN_FRAMES = 60;        // 1s minimum for fighting phase
const RETURNING_DURATION = 45;         // frames before returning -> idle

// ---------------------------------------------------------------------------
// Module-level state (reset on game restart via resetSafeZone)
// ---------------------------------------------------------------------------

let tauntActive = false;
let tauntTimer = 0;

// ---------------------------------------------------------------------------
// drawSafeZone
// ---------------------------------------------------------------------------

export function drawSafeZone(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  safeZoneUses: number,
  clearAllPhase: GameState['clearAllPhase']
): void {
  // Don't draw zone while clear-all sequence is active
  if (clearAllPhase !== 'idle') return;

  const x = cssW - SAFE_ZONE_WIDTH;
  const depleted = safeZoneUses === 3;

  // Background
  ctx.fillStyle = depleted ? 'rgba(80,80,80,0.06)' : 'rgba(255,255,255,0.08)';
  ctx.fillRect(x, 0, SAFE_ZONE_WIDTH, cssH);

  // Left border line
  ctx.strokeStyle = depleted ? 'rgba(80,80,80,0.25)' : 'rgba(200,200,200,0.4)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, cssH);
  ctx.stroke();

  // "safe" label centered in zone
  ctx.fillStyle = depleted ? 'rgba(80,80,80,0.25)' : 'rgba(200,200,200,0.5)';
  ctx.font = '11px "Courier New", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('safe', x + SAFE_ZONE_WIDTH / 2, cssH / 2);

  // Use count dots (3 dots stacked near bottom)
  const dotRadius = 2.5;
  const dotSpacing = 14;
  const dotX = x + SAFE_ZONE_WIDTH / 2;
  const dotBaseY = cssH - 60;
  const remaining = 3 - safeZoneUses;

  for (let i = 0; i < 3; i++) {
    const dotY = dotBaseY + i * dotSpacing;
    ctx.beginPath();
    ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2);
    if (i < remaining) {
      // Filled = remaining uses
      ctx.fillStyle = 'rgba(200,200,200,0.6)';
      ctx.fill();
    } else {
      // Empty = consumed
      ctx.strokeStyle = 'rgba(80,80,80,0.4)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  // Floating taunt box
  if (tauntActive) {
    const tx = 8;
    const ty = 8;
    const tw = 220;
    const th = 44;

    ctx.fillStyle = 'rgba(10,10,10,0.88)';
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(tx, ty, tw, th, 6);
    } else {
      ctx.rect(tx, ty, tw, th);
    }
    ctx.fill();
    ctx.strokeStyle = 'rgba(200,200,200,0.3)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    ctx.fillStyle = '#e0e0e0';
    ctx.font = '11px "Courier New", monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(SAFE_ZONE_TAUNT, tx + 10, ty + 10);
  }
}

// ---------------------------------------------------------------------------
// checkSafeZoneEntry — edge-trigger on cursor entering the zone
// ---------------------------------------------------------------------------

export function checkSafeZoneEntry(
  state: GameState,
  cssW: number,
  _cssH: number
): void {
  const insideZone = state.mouseX >= (cssW - SAFE_ZONE_WIDTH);

  if (!insideZone) {
    state.safeZoneEntered = false;
    return;
  }

  // Already inside — maintain flag, no re-trigger
  if (state.safeZoneEntered) return;

  // Edge trigger: just entered
  state.safeZoneEntered = true;

  // Don't trigger during active clear-all sequence
  if (state.clearAllPhase !== 'idle') return;

  // Case A: depleted (4th attempt) — floating taunt
  if (state.safeZoneUses === 3) {
    tauntActive = true;
    tauntTimer = SAFE_ZONE_TAUNT_DURATION;
    return;
  }

  // Case B: no enemies — no effect
  if (state.enemies.length === 0) return;

  // Case C: activate clear-all
  queueLine(SAFE_ZONE_QUIPS[state.safeZoneUses]);
  state.clearAllPhase = 'fighting';
  state.clearAllTimer = 0;
  setCharacterState('wave');
  state.safeZoneUses++;
  setSafeZoneLevel(state.safeZoneUses);
}

// ---------------------------------------------------------------------------
// tickSafeZone — advance clear-all state machine + taunt timer
// ---------------------------------------------------------------------------

export function tickSafeZone(state: GameState, dt: number): void {
  // Tick taunt timer independently of clear-all phase
  if (tauntActive) {
    tauntTimer -= dt * 60;
    if (tauntTimer <= 0) {
      tauntActive = false;
      tauntTimer = 0;
    }
  }

  if (state.clearAllPhase === 'idle') return;

  state.clearAllTimer += dt * 60;

  if (state.clearAllPhase === 'fighting') {
    // Stagger: explode one enemy each time timer crosses a STAGGER_INTERVAL boundary
    if (state.enemies.length > 0) {
      const prevFrame = state.clearAllTimer - dt * 60;
      const prevSlot = Math.floor(Math.max(0, prevFrame) / STAGGER_INTERVAL);
      const currSlot = Math.floor(state.clearAllTimer / STAGGER_INTERVAL);

      if (currSlot > prevSlot) {
        const enemy = state.enemies.shift()!;
        const wordLen = enemy.word.length;
        for (let i = 0; i < wordLen; i++) {
          const letterX = enemy.x + (i * 8) - (wordLen * 4);
          spawnParticle(
            enemy.word[i],
            letterX,
            enemy.y,
            state.particles,
            (Math.random() - 0.5) * 6,
            -(2 + Math.random() * 4),
            enemy.fontSize
          );
        }
      }
    }

    // Transition to returning when all enemies gone and minimum time elapsed
    if (state.enemies.length === 0 && state.clearAllTimer > FIGHTING_MIN_FRAMES) {
      state.clearAllPhase = 'returning';
      state.clearAllTimer = 0;
    }
  } else if (state.clearAllPhase === 'returning') {
    if (state.clearAllTimer >= RETURNING_DURATION) {
      setCharacterState('idle');
      state.clearAllPhase = 'idle';
      state.clearAllTimer = 0;
    }
  }
}

// ---------------------------------------------------------------------------
// resetSafeZone — reset module-level state on game restart
// ---------------------------------------------------------------------------

export function resetSafeZone(): void {
  tauntActive = false;
  tauntTimer = 0;
}
